/**
 * Structured logging utility for Wellspring
 *
 * Provides a consistent logging interface for both frontend and backend code.
 * Backend uses Pino directly, frontend uses this structured console wrapper.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogContext {
  [key: string]: unknown;
}

/**
 * Generate a unique request/correlation ID for tracing
 */
export function generateCorrelationId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Structured logger interface
 */
export interface Logger {
  debug(context: LogContext, message: string): void;
  debug(message: string): void;
  info(context: LogContext, message: string): void;
  info(message: string): void;
  warn(context: LogContext, message: string): void;
  warn(message: string): void;
  error(context: LogContext, message: string): void;
  error(message: string): void;
}

/**
 * Create a structured console logger for frontend use
 *
 * This provides a Pino-like interface but uses console methods
 * for browser compatibility.
 */
export function createLogger(componentName?: string): Logger {
  // globalThis is window in browsers and also works in Deno, which has no window.
  // Under Deno the level is only set programmatically (globalThis.LOG_LEVEL);
  // the LOG_LEVEL environment variable is not read here.
  const logLevel = (globalThis as typeof globalThis & { LOG_LEVEL?: string }).LOG_LEVEL || 'info';

  const levels: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  };

  const currentLevel = levels[logLevel as LogLevel] ?? levels.info;

  function shouldLog(level: LogLevel): boolean {
    return levels[level] >= currentLevel;
  }

  function log(
    level: LogLevel,
    contextOrMessage: LogContext | string,
    maybeMessage?: string,
  ): void {
    if (!shouldLog(level)) return;

    const timestamp = new Date().toISOString();
    const context: LogContext = typeof contextOrMessage === 'string' ? {} : contextOrMessage;
    const message = typeof contextOrMessage === 'string' ? contextOrMessage : maybeMessage || '';

    const logData = {
      timestamp,
      level,
      ...(componentName && { component: componentName }),
      ...context,
      message,
    };

    // Use appropriate console method
    const consoleMethod = level === 'debug' ? 'log' : level;
    console[consoleMethod](JSON.stringify(logData));
  }

  return {
    debug(contextOrMessage: LogContext | string, maybeMessage?: string) {
      log('debug', contextOrMessage, maybeMessage);
    },
    info(contextOrMessage: LogContext | string, maybeMessage?: string) {
      log('info', contextOrMessage, maybeMessage);
    },
    warn(contextOrMessage: LogContext | string, maybeMessage?: string) {
      log('warn', contextOrMessage, maybeMessage);
    },
    error(contextOrMessage: LogContext | string, maybeMessage?: string) {
      log('error', contextOrMessage, maybeMessage);
    },
  };
}

/**
 * Default logger instance
 */
export const logger = createLogger();
